import { PDFDocument, rgb } from 'pdf-lib';
import type { CardDesignData, CardFormat } from '@flashcard/shared';
import { buildQrValue } from './qr';

const MM_TO_PT = 72 / 25.4;
const BLEED = 3; // mm rundum

// Kartenmaße ohne Beschnitt (mm)
export const CARD_SIZE: Record<CardFormat, { width: number; height: number }> = {
  standard: { width: 85, height: 55 },
  round: { width: 55, height: 55 },
  square: { width: 55, height: 55 },
};

// Sichere Zone (Safe-Zone): 3 mm Innenabstand
export const SAFE_MARGIN = 3; // mm

function hexToRgb(hex: string) {
  const m = hex.replace('#', '');
  const r = parseInt(m.substring(0, 2), 16) / 255;
  const g = parseInt(m.substring(2, 4), 16) / 255;
  const b = parseInt(m.substring(4, 6), 16) / 255;
  return rgb(r, g, b);
}

export async function generateCardPdf(
  design: CardDesignData,
  assets: { logoBytes?: Uint8Array | null; qrBytes?: Uint8Array | null } = {},
): Promise<Uint8Array> {
  const base = CARD_SIZE[design.format];
  // Gesamtmaß mit Beschnitt
  const pageW = base.width + BLEED * 2;
  const pageH = base.height + BLEED * 2;

  const doc = await PDFDocument.create();
  const page = doc.addPage([pageW * MM_TO_PT, pageH * MM_TO_PT]);

  // Hintergrund bis zum Bleed-Rand
  const bg = hexToRgb(design.backgroundColor || '#ffffff');
  page.drawRectangle({
    x: 0,
    y: 0,
    width: pageW * MM_TO_PT,
    height: pageH * MM_TO_PT,
    color: bg,
  });

  // Koordinaten-Verschiebung um den Beschnitt (Inhalt startet bei BLEED)
  const ox = BLEED; // mm
  const oy = BLEED;

  // Logo
  if (design.logoUrl && design.logoBox && assets.logoBytes) {
    const logo = await doc.embedPng(assets.logoBytes);
    page.drawImage(logo, {
      x: (ox + design.logoBox.x) * MM_TO_PT,
      // PDF y ist von unten; konvertieren
      y: (pageH - (oy + design.logoBox.y) - design.logoBox.height) * MM_TO_PT,
      width: design.logoBox.width * MM_TO_PT,
      height: design.logoBox.height * MM_TO_PT,
    });
  }

  // QR-Code
  if (design.qrCode && assets.qrBytes) {
    const qr = await doc.embedPng(assets.qrBytes);
    page.drawImage(qr, {
      x: (ox + design.qrCode.x) * MM_TO_PT,
      y: (pageH - (oy + design.qrCode.y) - design.qrCode.size) * MM_TO_PT,
      width: design.qrCode.size * MM_TO_PT,
      height: design.qrCode.size * MM_TO_PT,
    });
  }

  // Texte
  for (const text of design.texts) {
    if (!text.content.trim()) continue;
    const color = hexToRgb(text.color || '#000000');
    const fontSizePt = text.fontSize;
    // Mittige X-Ausrichtung approximieren (ohne Font-Metrik: einfache Zentrierung)
    const approximateWidth = text.content.length * fontSizePt * 0.55;
    let x = (ox + text.x) * MM_TO_PT;
    if (text.align === 'center') x -= approximateWidth / 2;
    else if (text.align === 'right') x -= approximateWidth;
    page.drawText(text.content, {
      x,
      y: (pageH - (oy + text.y) - fontSizePt * 0.75) * MM_TO_PT,
      size: fontSizePt,
      color,
    });
  }

  return doc.save();
}

export function designQrValue(design: CardDesignData): string | null {
  return design.qrCode ? buildQrValue(design.qrCode.type, design.qrCode.value) : null;
}

// Data-URL oder externe URL → Uint8Array (für pdf-lib-embedPng)
export async function urlToBytes(url: string): Promise<Uint8Array> {
  if (url.startsWith('data:')) {
    const base64 = url.split(',')[1];
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return bytes;
  }
  const res = await fetch(url);
  const buf = await res.arrayBuffer();
  return new Uint8Array(buf);
}
